import React from 'react';
import { h, C, txt, n2, fmtRpm, fmtNm, fmtKw, fmtSigned, useElementWidth, useLinkedSelection } from './svgKit';
import { BLOCK_INFO, STATE_LABELS } from '../../../lib/powerSplitTools';

// THS power-split architecture: engine -> damper -> planetary (carrier), MG1 on the sun,
// ring -> counter gear / final drive -> differential -> wheels, MG2 -> reduction -> ring,
// and the electrical side MG1 <-> inverter <-> MG2 with the boost converter and HV battery.
//
// Every block shows its live values. Every flow is drawn from source to sink in the
// direction power actually flows (reversed when the power is negative), with stroke width
// proportional to |P|. Mechanical = solid, electrical = dashed amber; zero = thin grey.
// Two layouts: landscape (>= 560 px) and portrait for phones, with abbreviated titles.

const NARROW_BELOW_PX = 560;
const ZERO_KW = 0.01;

const LAYOUTS = {
  wide: {
    vw: 760,
    vh: 348,
    title: 11.5,
    value: 11,
    nodes: {
      engine: { x: 16, y: 20, w: 106, h: 72 },
      damper: { x: 150, y: 38, w: 50, h: 36 },
      psd: { x: 228, y: 20, w: 130, h: 72 },
      counter: { x: 394, y: 20, w: 106, h: 72 },
      diff: { x: 528, y: 38, w: 50, h: 36 },
      wheels: { x: 606, y: 20, w: 138, h: 72 },
      mg1: { x: 228, y: 140, w: 130, h: 72 },
      reduction: { x: 394, y: 140, w: 106, h: 72 },
      mg2: { x: 540, y: 140, w: 120, h: 72 },
      battery: { x: 16, y: 268, w: 120, h: 72 },
      boost: { x: 170, y: 286, w: 92, h: 36 },
      inverter: { x: 394, y: 268, w: 106, h: 72 },
    },
    edges: {
      engineDamper: [[122, 56], [150, 56]],
      damperPsd: [[200, 56], [228, 56]],
      psdCounter: [[358, 56], [394, 56]],
      counterDiff: [[500, 56], [528, 56]],
      diffWheels: [[578, 56], [606, 56]],
      psdMg1: [[293, 92], [293, 140]],
      mg2Reduction: [[540, 176], [500, 176]],
      reductionCounter: [[447, 140], [447, 92]],
      mg1Inverter: [[293, 212], [293, 240], [420, 240], [420, 268]],
      inverterMg2: [[500, 304], [600, 304], [600, 212]],
      batteryBoost: [[136, 304], [170, 304]],
      boostInverter: [[262, 304], [394, 304]],
    },
  },
  narrow: {
    vw: 324,
    vh: 452,
    title: 10.5,
    value: 10,
    nodes: {
      engine: { x: 6, y: 4, w: 92, h: 64 },
      damper: { x: 6, y: 92, w: 92, h: 32 },
      psd: { x: 6, y: 148, w: 92, h: 64 },
      counter: { x: 6, y: 240, w: 92, h: 64 },
      diff: { x: 6, y: 328, w: 92, h: 32 },
      wheels: { x: 6, y: 384, w: 92, h: 64 },
      mg1: { x: 116, y: 148, w: 92, h: 64 },
      reduction: { x: 116, y: 240, w: 92, h: 64 },
      mg2: { x: 226, y: 240, w: 92, h: 64 },
      battery: { x: 226, y: 4, w: 92, h: 64 },
      boost: { x: 226, y: 90, w: 92, h: 36 },
      inverter: { x: 226, y: 148, w: 92, h: 64 },
    },
    edges: {
      engineDamper: [[52, 68], [52, 92]],
      damperPsd: [[52, 124], [52, 148]],
      psdCounter: [[52, 212], [52, 240]],
      counterDiff: [[52, 304], [52, 328]],
      diffWheels: [[52, 360], [52, 384]],
      psdMg1: [[98, 180], [116, 180]],
      mg2Reduction: [[226, 272], [208, 272]],
      reductionCounter: [[116, 272], [98, 272]],
      mg1Inverter: [[208, 180], [226, 180]],
      inverterMg2: [[272, 212], [272, 240]],
      batteryBoost: [[272, 68], [272, 90]],
      boostInverter: [[272, 126], [272, 148]],
    },
  },
};

const TITLES = {
  wide: {
    engine: 'Engine', damper: 'Damper', psd: 'Planetary set', counter: 'Counter / FD', diff: 'Diff',
    wheels: 'Wheels', mg1: 'MG1', reduction: 'MG2 reduction', mg2: 'MG2', battery: 'HV battery',
    boost: 'Boost conv.', inverter: 'Inverter',
  },
  narrow: {
    engine: 'ICE', damper: 'Damper', psd: 'Planetary', counter: 'Counter/FD', diff: 'Diff',
    wheels: 'Wheels', mg1: 'MG1', reduction: 'MG2 red.', mg2: 'MG2', battery: 'HV batt',
    boost: 'Boost', inverter: 'Inverter',
  },
};

const ABBREVIATIONS = [
  ['ICE', 'Engine (internal-combustion engine)'],
  ['Planetary', 'Planetary gear set (power-split device)'],
  ['Counter/FD', 'Counter gear / final drive'],
  ['MG2 red.', 'MG2 reduction gear'],
  ['Diff', 'Differential'],
  ['HV batt', 'High-voltage battery'],
  ['Boost', 'Boost converter'],
];

const STATE_TAG = {
  motoring: 'MOT', generating: 'GEN', idle: 'IDLE', driving: 'ON', motored: 'DRIVEN', stopped: 'OFF',
  spinning: 'SPIN', discharging: 'OUT', charging: 'IN',
};

const NODE_ACCENT = { engine: C.carrier, mg1: C.sun, psd: C.ring, mg2: C.mg2, battery: C.elec, inverter: C.elec, boost: C.elec };

function edgePowers(r) {
  return {
    engineDamper: { kw: r.engine.powerKw, kind: 'mech' },
    damperPsd: { kw: r.engine.powerKw, kind: 'mech' },
    psdMg1: { kw: -r.mg1.powerKw, kind: 'mech' },
    psdCounter: { kw: r.ring.powerKw, kind: 'mech' },
    mg2Reduction: { kw: r.mg2.powerKw, kind: 'mech' },
    reductionCounter: { kw: r.mg2.powerKw, kind: 'mech' },
    counterDiff: { kw: r.output.powerKw, kind: 'mech' },
    diffWheels: { kw: r.output.powerKw, kind: 'mech' },
    mg1Inverter: { kw: -r.mg1.elecKw, kind: 'elec' },
    inverterMg2: { kw: r.mg2.elecKw, kind: 'elec' },
    batteryBoost: { kw: r.battery.powerKw, kind: 'elec' },
    boostInverter: { kw: r.battery.powerKw, kind: 'elec' },
  };
}

function nodeValues(id, r) {
  const o = r.output;
  switch (id) {
    case 'engine': return [fmtRpm(r.engine.rpm), fmtNm(r.engine.torqueNm), fmtKw(r.engine.powerKw)];
    case 'mg1': return [fmtRpm(r.mg1.rpm), fmtNm(r.mg1.torqueNm), fmtKw(r.mg1.powerKw)];
    case 'mg2': return [fmtRpm(r.mg2.rpm), fmtNm(r.mg2.torqueNm), fmtKw(r.mg2.powerKw)];
    case 'psd': return [`R ${fmtRpm(r.ring.rpm)}`, `${fmtNm(r.ring.torqueNm)} out`, fmtKw(r.ring.powerKw)];
    case 'reduction': return [`${fmtSigned(r.mg2.ratio, 3)} : 1`, `${fmtNm(r.mg2.torqueNm * r.mg2.ratio)}`, 'at ring'];
    case 'counter': return o.axleTorqueNm === null
      ? [`${fmtNm(o.ringEqTorqueNm)}`, 'at ring', 'no ratio set']
      : [`${fmtNm(o.ringEqTorqueNm)} in`, `${fmtNm(o.axleTorqueNm)}`, 'at axle'];
    case 'wheels': return o.vehicleSpeedKmh === null
      ? [fmtKw(o.powerKw), 'speed n/a', '']
      : [`${fmtSigned(o.vehicleSpeedKmh, 1)} km/h`, fmtRpm(o.wheelRpm), fmtKw(o.powerKw)];
    case 'battery': return [fmtKw(r.battery.powerKw), STATE_LABELS[r.battery.state], ''];
    case 'inverter': return [`MG1 ${fmtKw(r.inverter.mg1Kw)}`, `MG2 ${fmtKw(r.inverter.mg2Kw)}`, `loss ${fmtSigned(r.inverter.lossKw, 2)}`];
    case 'boost': return [];
    default: return [];
  }
}

function nodeState(id, r) {
  if (id === 'engine') return r.engine.state;
  if (id === 'mg1') return r.mg1.state;
  if (id === 'mg2') return r.mg2.state;
  if (id === 'battery') return r.battery.state;
  return null;
}

// Arrow along a polyline: the last `head` px are a filled triangle, the rest a stroked path.
function arrowGeometry(points, head) {
  const pts = points.map((p) => [...p]);
  const [x2, y2] = pts[pts.length - 1];
  const [x1, y1] = pts[pts.length - 2];
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const ux = (x2 - x1) / len;
  const uy = (y2 - y1) / len;
  const headLen = Math.min(head, len * 0.8);
  const bx = x2 - ux * headLen;
  const by = y2 - uy * headLen;
  pts[pts.length - 1] = [bx, by];
  const half = headLen * 0.62;
  const tri = `M${n2(x2)},${n2(y2)} L${n2(bx - uy * half)},${n2(by + ux * half)} L${n2(bx + uy * half)},${n2(by - ux * half)} Z`;
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${n2(x)},${n2(y)}`).join(' ');
  return { d, tri };
}

function renderEdge(key, points, { kw, kind }, maxKw, animate) {
  const zero = Math.abs(kw) < ZERO_KW;
  const pts = kw < 0 ? [...points].reverse() : points;
  const d0 = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${n2(x)},${n2(y)}`).join(' ');
  const testProps = { 'data-testid': `ps-flow-${key}`, 'data-kw': kw, 'data-zero': zero ? '1' : '0', 'data-kind': kind };
  if (zero) {
    return h('path', { key, d: d0, fill: 'none', ...testProps, style: { stroke: C.faint, strokeWidth: 1.2, strokeDasharray: '2 3' } });
  }
  const width = 1.6 + 4.6 * Math.min(1, Math.abs(kw) / maxKw);
  const color = kind === 'elec' ? C.elec : C.fg;
  const { d, tri } = arrowGeometry(pts, 7 + width * 1.3);
  const children = [
    h('path', {
      key: 'line', d, fill: 'none',
      className: kind === 'elec' && animate ? 'ps-flow-anim' : undefined,
      style: { stroke: color, strokeWidth: width, strokeDasharray: kind === 'elec' ? '7 5' : undefined, strokeLinejoin: 'round' },
    }),
    h('path', { key: 'head', d: tri, style: { fill: color } }),
  ];
  if (kind === 'mech' && animate) {
    children.splice(1, 0, h('path', {
      key: 'march', d, fill: 'none', className: 'ps-flow-anim',
      style: { stroke: C.card, strokeOpacity: 0.7, strokeWidth: Math.max(1, width * 0.45), strokeDasharray: '3 9', strokeLinecap: 'round' },
    }));
  }
  return h('g', { key, ...testProps, 'data-dir': kw < 0 ? 'reverse' : 'forward', 'data-width': n2(width) }, ...children);
}

function renderNode(id, box, layoutKey, r, active, onSel) {
  const L = LAYOUTS[layoutKey];
  const title = TITLES[layoutKey][id];
  const accent = NODE_ACCENT[id] || C.muted;
  const isActive = active === id;
  const greyed = id === 'boost' && !r.boostFitted;
  const state = nodeState(id, r);
  const values = nodeValues(id, r);
  const cx = box.x + box.w / 2;
  const small = box.h < 50;
  const children = [
    h('rect', {
      key: 'box', x: box.x, y: box.y, width: box.w, height: box.h, rx: 5,
      style: {
        fill: isActive ? C.mutedBg : C.card,
        stroke: isActive ? C.primary : accent,
        strokeWidth: isActive ? 2.4 : 1.5,
        strokeDasharray: greyed ? '4 3' : undefined,
      },
    }),
    h('rect', { key: 'accent', x: box.x, y: box.y, width: 4, height: box.h, rx: 2, style: { fill: accent } }),
  ];
  if (small) {
    // Boost has a second line (fitted / not fitted); the others are centred titles.
    const hasSub = id === 'boost';
    children.push(txt('t', cx, hasSub ? box.y + 15 : box.y + box.h / 2 + 4, title, { anchor: 'middle', size: L.title, weight: 700 }));
    if (hasSub) {
      children.push(txt('s', cx, box.y + 29, greyed ? 'not fitted' : 'fitted', { anchor: 'middle', size: L.value - 1, color: C.muted }));
    }
  } else {
    children.push(txt('t', box.x + 9, box.y + 15, title, { size: L.title, weight: 700 }));
    if (state) {
      children.push(txt('st', box.x + box.w - 6, box.y + 15, STATE_TAG[state] || '', {
        anchor: 'end', size: L.value - 1.5, weight: 700, color: state === 'idle' || state === 'stopped' ? C.muted : accent,
      }));
    }
    const lineH = layoutKey === 'wide' ? 15 : 14;
    values.forEach((v, i) => {
      if (v) children.push(txt(`v${i}`, box.x + 9, box.y + 32 + i * lineH, v, { size: L.value, color: i === 2 ? C.fg : C.muted, weight: i === 2 ? 700 : 400 }));
    });
  }
  return h('g', {
    key: id,
    role: 'button',
    tabIndex: 0,
    'aria-label': `${BLOCK_INFO[id].name}: what it does`,
    'aria-pressed': isActive,
    'data-testid': `ps-block-${id}`,
    className: 'cursor-pointer outline-none',
    opacity: greyed ? 0.45 : 1,
    onMouseEnter: () => onSel('hover', id),
    onMouseLeave: () => onSel('leave', id),
    onFocus: () => onSel('focus', id),
    onBlur: () => onSel('blur', id),
    onClick: () => onSel('tap', id),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSel('tap', id); }
    },
  }, ...children);
}

const ArchitectureDiagram = ({ r, animate, highlight }) => {
  const [wrapRef, width] = useElementWidth();
  const [active, onSel] = useLinkedSelection();
  const layoutKey = width && width < NARROW_BELOW_PX ? 'narrow' : 'wide';
  const L = LAYOUTS[layoutKey];
  const flows = edgePowers(r);
  const maxKw = Math.max(1, ...Object.values(flows).map((f) => Math.abs(f.kw)));

  const svg = h('svg', {
    viewBox: `0 0 ${L.vw} ${L.vh}`,
    className: 'w-full h-auto select-none',
    role: 'img',
    'aria-label': 'Power-split hybrid architecture with live power flows',
    'data-testid': 'ps-architecture-svg',
    'data-layout': layoutKey,
    style: { maxWidth: L.vw * 1.25 },
  },
  h('g', { key: 'edges' }, ...Object.entries(L.edges).map(([k, pts]) => renderEdge(k, pts, flows[k], maxKw, animate))),
  h('g', { key: 'nodes' }, ...Object.entries(L.nodes).map(([id, box]) => renderNode(id, box, layoutKey, r, active || highlight, onSel))));

  const info = active ? BLOCK_INFO[active] : null;
  return (
    <div className="space-y-3">
      <div ref={wrapRef} className="w-full flex justify-center" data-testid="ps-architecture">{svg}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="ps-architecture-legend">
        <span className="flex items-center gap-1.5">
          <svg width="30" height="8" aria-hidden="true"><line x1="0" y1="4" x2="30" y2="4" strokeWidth="3" style={{ stroke: C.fg }} /></svg>
          Mechanical power
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="30" height="8" aria-hidden="true"><line x1="0" y1="4" x2="30" y2="4" strokeWidth="3" strokeDasharray="7 5" style={{ stroke: C.elec }} /></svg>
          Electrical power
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="30" height="8" aria-hidden="true"><line x1="0" y1="4" x2="30" y2="4" strokeWidth="1.2" strokeDasharray="2 3" style={{ stroke: C.faint }} /></svg>
          No power
        </span>
        <span>Arrow = direction of power flow; thickness ∝ kW.</span>
        <span>Tags: MOT motoring · GEN generating · ON engine driving · IN/OUT battery charging/discharging.</span>
      </div>
      {layoutKey === 'narrow' && (
        <dl className="grid grid-cols-1 gap-0.5 text-xs text-muted-foreground" data-testid="ps-architecture-abbrev">
          {ABBREVIATIONS.map(([a, full]) => (
            <div key={a} className="flex gap-2"><dt className="font-mono font-bold text-foreground w-20 shrink-0">{a}</dt><dd>{full}</dd></div>
          ))}
        </dl>
      )}
      <div className="rounded-md border border-structure bg-muted/40 p-3 text-sm min-h-[4.5rem]" data-testid="ps-block-info" aria-live="polite">
        {info ? (
          <>
            <p className="font-bold">{info.name}</p>
            <p className="text-muted-foreground text-xs leading-relaxed mt-1">{info.text}</p>
          </>
        ) : (
          <p className="text-muted-foreground text-xs">Tap, hover or focus any block to see what it does.</p>
        )}
      </div>
    </div>
  );
};

export default ArchitectureDiagram;
